import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { ChangeAssignmentStatusTask } from '../Tasks/ChangeAssignmentStatusTask'
import { ChangeExecutorStatusTask } from '../Tasks/ChangeExecutorStatusTask'
import { validateForTransportDistributed } from '../Helpers/validateAssignments'

/**
 * Стор перегляду потреб у картці заявки (`VehicleNeeds`, режим перегляду).
 *
 * Тримає: прапорці запитів дій диспетчера (зміна статусу завдання/виконавця) і помилки
 * перевірки перед «Транспорт розподілено» (`invalidAssignments` → підсвітка комірок).
 * Запити йдуть через `Tasks/`, результат — через `Notify`; після успіху викликається
 * `reload` (перезавантаження картки).
 */
export class VehicleNeedsViewStore {
    changingStatus = false
    takeInWorkLoading: string | null = null
    invalidAssignments: Record<string, Set<string>> = {}

    constructor() {
        makeAutoObservable(this)
    }

    async changeStatus(taskUuid: string, status: string, notify: Notify, reload?: () => void): Promise<void> {
        if (this.changingStatus) return
        this.changingStatus = true
        try {
            await ChangeAssignmentStatusTask(taskUuid, status)
            notify.success('Статус змінено')
            reload?.()
        } catch (e: any) {
            const msg = e?.response?.message || e?.response?.detail || e?.message || 'Не вдалося змінити статус'
            notify.error(msg)
            reload?.()
        } finally {
            runInAction(() => {
                this.changingStatus = false
            })
        }
    }

    async changeExecutorStatus(
        executorUuid: string,
        newStatus: string,
        notify: Notify,
        reload?: () => void
    ): Promise<void> {
        this.takeInWorkLoading = executorUuid
        try {
            await ChangeExecutorStatusTask(executorUuid, newStatus)
            notify.success('Статус змінено')
            reload?.()
        } catch (e: any) {
            notify.error(e?.message || 'Не вдалося змінити статус')
        } finally {
            runInAction(() => {
                this.takeInWorkLoading = null
            })
        }
    }

    /**
     * `Транспорт розподілено` action — validates required fields across all
     * executor assignments first. On validation failure the view shows the
     * `invalidAssignments` map; on success it triggers an executor-status change.
     */
    async clickTransportDistributed(
        executorUuid: string,
        assignments: any[],
        vn: any,
        notify: Notify,
        reload?: () => void
    ): Promise<void> {
        const errors = validateForTransportDistributed(assignments, vn)
        if (Object.keys(errors).length > 0) {
            this.invalidAssignments = errors
            notify.error('Заповніть всі обов\'язкові поля завдань перед встановленням статусу "Транспорт розподілено"')
            return
        }
        this.invalidAssignments = {}
        await this.changeExecutorStatus(executorUuid, 'transport_distributed', notify, reload)
    }
}
